import FieldHint from '@/components/common/FieldHint';
import { Input, Select, Textarea } from '@/components/ui/FormControls';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useConfirm } from '@/contexts/ConfirmContext';
import { useProduct, type ProductWithVariants } from '@/hooks/useProducts';
import { useOperations, useOperation } from '@/hooks/useOperations';
import { useOperationIntent } from '@/hooks/useOperationIntent';
import {
  useCatalogChoices,
  useCatalogProfile,
  catalogIssues,
  type CatalogValues,
  type CatalogField,
} from '@/hooks/useCatalog';
import CatalogFields, { catalogInput as input } from '@/components/products/CatalogFields';
import PageHeader from '@/components/common/PageHeader';
import LoadingState from '@/components/common/LoadingState';
import ErrorState from '@/components/common/ErrorState';
import { Copy } from 'lucide-react';
import { getNextSize, STANDARD_SIZE_SEQUENCES } from '@/utils/productCodeUtils';
import LegacyForm from './ProductFormPage';
import JerseySkuReview from '@/components/products/JerseySkuReview';
import { useJerseyIdentifiers } from '@/hooks/useJerseyIdentifiers';
import { useJerseyEntry, useJerseyEntrySave, useJerseyDraftCode } from '@/hooks/useJerseyEntry';
import JerseyDetailsFields from '@/components/products/JerseyDetailsFields';
import JerseyGenerationMode from '@/components/products/JerseyGenerationMode';
import { OperationError } from '@/lib/operations';
import {
  assertJerseyReview,
  jerseyCodePreview,
  jerseySkuPreview,
  type JerseySkuReview as JerseyReview,
  jerseyNameSuggestion,
  jerseyEntryPrefix,
  jerseyEntrySku,
  jerseyEntryIssues,
  nextUnusedJerseySize,
  JERSEY_HELPER_CODES,
  type JerseyDetailsV2,
  type JerseyOutputMode,
} from '@klothiq/domain';
type Variant = {
  key: string;
  id?: string;
  sku: string;
  sku_mode?: JerseyOutputMode;
  updated_at?: string;
  size: string;
  color: string;
  default_cost: number;
  default_price: number;
  stock_quantity: number;
  low_stock_level: number;
  attributes: CatalogValues;
};
const emptyVariant = (): Variant => ({
  key: crypto.randomUUID(),
  sku: '',
  sku_mode: 'auto',
  size: '',
  color: '',
  default_cost: 0,
  default_price: 0,
  stock_quantity: 0,
  low_stock_level: 5,
  attributes: {},
});
export default function ProductWorkspaceRoute() {
  const ops = useOperations();
  const { id } = useParams();
  if (ops.isLoading) return <LoadingState />;
  if (ops.error) return <ErrorState message={ops.error.message} onRetry={ops.refetch} />;
  return ops.data?.modules.catalog === 1 ? (
    <ProductWorkspacePage key={id || 'new'} />
  ) : (
    <LegacyForm />
  );
}
function ProductWorkspacePage() {
  const { id } = useParams();
  const productQuery = useProduct(id);
  const existing = productQuery.data as
    | (ProductWithVariants & {
        schema_version_id?: string | null;
        jersey_entry?: { details?: JerseyDetailsV2 } | null;
      })
    | undefined;
  const choices = useCatalogChoices();
  const { can } = useAuth();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const intent = useOperationIntent(id || 'product-new');
  const save = useOperation<{ success: boolean; product_id: string }>('save_product_workspace_v1');
  const operations = useOperations();
  const saveEntry = useJerseyEntrySave();
  const busy = save.isPending || saveEntry.isPending;
  const [type, setType] = useState(''),
    [name, setName] = useState(''),
    [code, setCode] = useState(''),
    [category, setCategory] = useState(''),
    [audience, setAudience] = useState(''),
    [age, setAge] = useState(''),
    [description, setDescription] = useState(''),
    [attrs, setAttrs] = useState<CatalogValues>({}),
    [variants, setVariants] = useState<Variant[]>([emptyVariant()]),
    [error, setError] = useState(''),
    [errors, setErrors] = useState<Record<string, string>>({}),
    [removed, setRemoved] = useState<string[]>([]),
    [adopt, setAdopt] = useState(false);
  const [version, setVersion] = useState<string | null | undefined>(undefined);
  const [jerseyDetails, setJerseyDetails] = useState<JerseyDetailsV2>({});
  const [nameMode, setNameMode] = useState<JerseyOutputMode>('manual');
  const [codeMode, setCodeMode] = useState<JerseyOutputMode>('auto');
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  const categoryDrafts = useRef(
    new Map<
      string,
      {
        details: JerseyDetailsV2;
        nameMode: JerseyOutputMode;
        codeMode: JerseyOutputMode;
        name: string;
        code: string;
        attrs: CatalogValues;
        variants: Variant[];
        category: string;
      }
    >(),
  );
  const entry = useJerseyEntry(type, existing?.product_type_id === type ? id : undefined);
  const original = useRef<typeof existing>();
  const lock = useRef(false);
  const formRef = useRef<HTMLFormElement>(null);
  const jersey = useJerseyIdentifiers(type, existing?.product_type_id === type ? id : undefined);
  const [jerseyReview, setJerseyReview] = useState<JerseyReview | null>(null);
  const entryEnabled = entry.data?.enabled === true;
  const jerseyEnabled = !entryEnabled && jersey.data?.enabled === true;
  const needsJerseyReview = jerseyEnabled && (!id || variants.some((v) => !v.id));
  const activeReview = jerseyReview || jersey.data?.review || null;
  const suggestedName = entryEnabled ? jerseyNameSuggestion(jerseyDetails, entry.data!) : null;
  const effectiveName = entryEnabled && nameMode === 'auto' ? suggestedName || '' : name;
  const editingAvailable = operations.data?.modules.jersey_entry_edit === 1;
  const draftKey = intent.key('draft-code');
  const draftCode = useJerseyDraftCode(
    {
      profileId: type,
      configurationVersion: entry.data?.configuration_version_id || '',
      draftKey,
      prefix: entry.data ? jerseyEntryPrefix(jerseyDetails, entry.data) : '',
      name: effectiveName,
      details: jerseyDetails,
    },
    entryEnabled &&
      editingAvailable &&
      !id &&
      codeMode === 'auto' &&
      effectiveName.trim().length >= 2 &&
      online,
  );
  const entryCode = entryEnabled
    ? id
      ? code
      : codeMode === 'manual'
        ? code
        : effectiveName.trim().length >= 2
          ? draftCode.data?.product_code || ''
          : ''
    : '';
  let jerseyPreviewCode = '';
  if (jersey.data?.config && activeReview) {
    try {
      jerseyPreviewCode = jerseyCodePreview(
        jersey.data.config,
        activeReview.details,
        id ? code : undefined,
      );
    } catch {
      /* Review control explains the missing team. */
    }
  }
  function skuPreview(variant: Variant) {
    if (entryEnabled && !variant.id && (variant.sku_mode || 'auto') === 'auto') {
      if (!effectiveName.trim() || !entryCode) return '';
      try {
        return jerseyEntrySku(jerseyDetails, entryCode, variant.color, variant.size, entry.data!);
      } catch {
        return '';
      }
    }
    if (variant.id || !jerseyEnabled) return variant.sku;
    if (!jersey.data?.config || !activeReview || !jerseyPreviewCode) return '';
    try {
      return jerseySkuPreview(
        jersey.data.config,
        activeReview.details,
        jerseyPreviewCode,
        variant.color,
        variant.size,
      );
    } catch {
      return '';
    }
  }
  useEffect(() => {
    if (existing && !original.current) {
      original.current = existing;
      setType(existing.product_type_id || '');
      setVersion(existing.schema_version_id || null);
      setName(existing.name);
      setCode(existing.product_code || '');
      setCategory(existing.category_id || '');
      setAudience(existing.target_audience_id || '');
      setAge(existing.age_group_id || '');
      setDescription(existing.description || '');
      setAttrs((existing.attributes || {}) as CatalogValues);
      setJerseyDetails(existing.jersey_entry?.details || {});
      setVariants(
        existing.product_variants
          .filter((v) => v.is_active)
          .map((v) => ({
            ...v,
            key: v.id,
            stock_quantity: 0,
            default_cost: Number(v.default_cost),
            default_price: Number(v.default_price),
            color: v.color || '',
            attributes: (v.attributes || {}) as CatalogValues,
          })),
      );
    }
  }, [existing]);
  const profile = useCatalogProfile(type, version);
  const latestSchema = choices.data?.types.find((t) => t.id === type)?.current_schema_version_id;
  const definition = profile.data?.definition;
  const entryUnavailable =
    definition?.jersey_entry?.enabled === true &&
    (operations.data?.modules.jersey_entry !== 2 ||
      !!entry.error ||
      (!!entry.data && !entry.data.enabled));
  const fields = (definition?.fields || []).map((f) =>
    profile.data?.legacy && id ? { ...f, required: false } : f,
  );
  const legacyFields: CatalogField[] = Object.entries(attrs)
    .filter(
      ([key, val]) =>
        !fields.some((f) => f.code === key) && ['string', 'number', 'boolean'].includes(typeof val),
    )
    .map(([key, val]) => ({
      code: key,
      label: key.replaceAll('_', ' '),
      type: typeof val === 'number' ? 'number' : typeof val === 'boolean' ? 'boolean' : 'text',
      level: 'product',
      required: false,
    }));
  async function changeType(next: string) {
    if (next === type) return;
    if (
      (Object.keys(attrs).length || Object.keys(jerseyDetails).length || name || id) &&
      !(await confirm({
        title: 'Change product category?',
        description:
          'Saved details are retained. The new category may require additional fields before saving.',
        confirmText: 'Change category',
      }))
    )
      return;
    categoryDrafts.current.set(type, {
      details: jerseyDetails,
      nameMode,
      codeMode,
      name,
      code,
      attrs,
      variants,
      category,
    });
    const draft = categoryDrafts.current.get(next);
    setJerseyDetails(
      draft?.details ||
        (next === existing?.product_type_id ? existing.jersey_entry?.details || {} : {}),
    );
    setNameMode(draft?.nameMode || 'manual');
    setCodeMode(draft?.codeMode || 'auto');
    if (draft) {
      setName(draft.name);
      setCode(draft.code);
      setAttrs(draft.attrs);
      setVariants(draft.variants);
      setCategory(draft.category);
    } else setCategory('');
    setType(next);
    setJerseyReview(null);
    setAdopt(false);
    setVersion(
      next === original.current?.product_type_id ? original.current.schema_version_id : undefined,
    );
    setErrors({});
  }
  const updateVariant = (index: number, patch: Partial<Variant>) =>
    setVariants((all) => all.map((v, i) => (i === index ? { ...v, ...patch } : v)));
  function duplicateVariant(index: number) {
    if (lock.current || busy || variants.length >= 500) return;
    const source = variants[index];
    if (!source) return;
    const sizeField = fields.find((f) => f.level === 'variant' && f.code === 'SIZE');
    const sizeOptions =
      sizeField?.type === 'select'
        ? (sizeField.options
            ?.filter((option) => option.active !== false)
            .map((option) => option.value) ?? [])
        : [];
    let size = source.size;
    if (entryEnabled) {
      const order = entry.data!.entry_config.size_order.length
        ? entry.data!.entry_config.size_order
        : sizeOptions;
      const next = nextUnusedJerseySize(
        source,
        order,
        [...variants, ...entry.data!.occupied_variants],
        entry.data!.identifier_config,
      );
      if (!next) {
        toast.info(
          'No next unused size is available. Use Add variant to choose another size or colour.',
        );
        return;
      }
      size = next;
    }
    const used = new Set(
      variants.filter((v) => v.color === source.color).map((v) => v.size.trim().toUpperCase()),
    );
    if (!entryEnabled && sizeField?.type === 'select') {
      const current = sizeOptions.indexOf(source.size);
      if (current >= 0)
        size =
          sizeOptions
            .slice(current + 1)
            .find((candidate) => !used.has(candidate.trim().toUpperCase())) ?? source.size;
    } else if (!entryEnabled && source.size.trim() && (!sizeField || sizeField.type === 'text')) {
      // Restore the previous next-size shortcut without inventing an apparel size
      // for an empty or category-specific free-text size.
      let candidate = source.size;
      for (let attempt = 0; attempt < 500; attempt++) {
        if (
          !/^\d+$/.test(candidate) &&
          !STANDARD_SIZE_SEQUENCES.some((sequence) =>
            sequence.some((value) => value.toUpperCase() === candidate.trim().toUpperCase()),
          )
        )
          break;
        const next = getNextSize(candidate);
        if (next.trim().toUpperCase() === candidate.trim().toUpperCase()) break;
        candidate = next;
        if (!used.has(candidate.trim().toUpperCase())) {
          size = candidate;
          break;
        }
      }
    }
    const attributes = structuredClone(source.attributes);
    if (Object.prototype.hasOwnProperty.call(attributes, 'SIZE')) attributes.SIZE = size;
    const duplicate: Variant = {
      ...emptyVariant(),
      size,
      color: source.color,
      default_cost: source.default_cost,
      default_price: source.default_price,
      low_stock_level: source.low_stock_level,
      attributes,
    };
    setVariants((all) =>
      all.length >= 500 ? all : [...all.slice(0, index + 1), duplicate, ...all.slice(index + 1)],
    );
    setErrors({});
    toast.info(
      `Variant duplicated${size !== source.size ? ` with size ${size}` : ''}. Review the size; opening stock is zero and a new SKU will be generated when saved.`,
    );
    requestAnimationFrame(() => {
      const card = formRef.current?.querySelector<HTMLElement>(
        `[data-variant-key="${duplicate.key}"]`,
      );
      card?.scrollIntoView({ block: 'nearest' });
      (
        card?.querySelector<HTMLElement>('[data-variant-size], [id$="-SIZE"]') ??
        card?.querySelector<HTMLElement>('input:not([readonly])') ??
        card
      )?.focus({ preventScroll: true });
    });
  }
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (lock.current) return;
    if (!online) {
      setError('You are offline. Reconnect before saving; your entered values are kept.');
      return;
    }
    const issues: Record<string, string> = {};
    if (!entryEnabled && (jersey.isFetching || jersey.error))
      issues.jersey = 'Jersey identifier settings could not be confirmed. Retry before saving.';
    if (needsJerseyReview) {
      if (
        !activeReview?.confirmed ||
        activeReview.configuration_version_id !== jersey.data?.configuration_version_id
      )
        issues.jersey = 'Review and confirm the jersey SKU details.';
      else if (jersey.data?.config) {
        try {
          assertJerseyReview(jersey.data, activeReview);
          const previewCode = jerseyCodePreview(
            jersey.data.config,
            activeReview.details,
            id ? code : undefined,
          );
          for (const v of variants.filter((v) => !v.id))
            jerseySkuPreview(
              jersey.data.config,
              activeReview.details,
              previewCode,
              v.color,
              v.size,
            );
        } catch (e) {
          issues.jersey = (e as Error).message;
        }
      }
    }
    if (!type) issues.category = 'Choose a product category';
    if (effectiveName.trim().length < 2 || effectiveName.trim().length > 200)
      issues.name = 'Enter a product name of 2–200 characters, directly or using optional details.';
    if (entryEnabled) {
      for (const [key, message] of Object.entries(jerseyEntryIssues(jerseyDetails)))
        issues[`jersey-${key}`] = message;
      if (
        (codeMode === 'manual' || !!id) &&
        (!id || code !== original.current?.product_code) &&
        (!code.trim() || !/^[A-Z0-9][A-Z0-9-]*$/.test(code) || code.length > 160)
      )
        issues.code = 'Enter a code of at most 160 letters, numbers and hyphens, or choose Auto.';
      if (!id && codeMode === 'auto' && !entryCode)
        issues.code =
          draftCode.error?.message || 'Wait for a generated code, or enter one manually.';
      if (!editingAvailable)
        issues.jersey =
          'The editable identifiers database update is required before saving. Your entries are kept.';
      if (entry.isFetching || entry.error)
        issues.jersey = 'Wait for current jersey settings or retry before saving.';
    }
    if (entryUnavailable)
      issues.jersey =
        'Optional jersey entry needs its compatible database update. Your entered values are kept.';
    if (!variants.length) issues.variants = 'Keep at least one active variant';
    const old = original.current;
    const sameProfile =
      !!old &&
      old.product_type_id === type &&
      (old.schema_version_id ?? null) === (profile.data?.schema_version_id ?? null);
    Object.assign(
      issues,
      catalogIssues(fields, attrs, sameProfile ? ((old?.attributes || {}) as CatalogValues) : {}),
    );
    variants.forEach((v, i) => {
      if (entryEnabled) {
        if (!v.size.trim() || v.size.length > 20)
          issues[`v${i}-size`] = 'Enter a valid variant size.';
        if (v.color.length > 50) issues[`v${i}-color`] = 'Use a colour of at most 50 characters.';
        if (
          (v.id
            ? v.sku !== original.current?.product_variants.find((p) => p.id === v.id)?.sku
            : (v.sku_mode || 'auto') === 'manual') &&
          (!v.sku.trim() || v.sku.length > 512 || /[\u0000-\u001f\u007f]/.test(v.sku))
        )
          issues[`v${i}-sku`] = 'Enter a SKU of at most 512 characters, or choose Auto.';
        if (!v.id && (v.sku_mode || 'auto') === 'auto' && skuPreview(v).length > 512)
          issues[`v${i}-sku`] =
            'The generated SKU is too long. Review its details or choose Manual.';
      }
      const prior = old?.product_variants.find((p) => p.id === v.id);
      const vs = catalogIssues(
        fields,
        { ...v.attributes, SIZE: v.size || 'Standard', COLOR: v.color },
        prior && sameProfile
          ? {
              ...((prior.attributes as CatalogValues) || {}),
              SIZE: prior.size,
              COLOR: prior.color || '',
            }
          : {},
        'variant',
      );
      Object.entries(vs).forEach(([k, s]) => (issues[`v${i}-${k}`] = s));
      if (
        ![v.default_cost, v.default_price].every(
          (n) => Number.isFinite(n) && n >= 0 && Math.abs(Math.round(n * 100) - n * 100) < 0.000001,
        ) ||
        !Number.isInteger(v.stock_quantity) ||
        v.stock_quantity < 0 ||
        !Number.isInteger(v.low_stock_level) ||
        v.low_stock_level < 0
      )
        issues[`v${i}`] = 'Check cost, price and whole stock quantities';
      if (issues[`v${i}`])
        for (const key of [
          'default_cost',
          'default_price',
          'stock_quantity',
          'low_stock_level',
        ] as const) {
          const n = v[key];
          if (
            !Number.isFinite(n) ||
            n < 0 ||
            (key === 'default_cost' || key === 'default_price'
              ? Math.abs(Math.round(n * 100) - n * 100) >= 0.000001
              : !Number.isInteger(n))
          )
            issues[`v${i}-${key}`] = 'Enter a nonnegative amount or whole quantity.';
        }
    });
    setErrors(issues);
    setError('');
    if (Object.keys(issues).length) {
      setError(Object.values(issues)[0]!);
      focusInvalid();
      return;
    }
    if (!profile.data?.schema_version_id && !id) {
      setError('Publish the category setup in Master Configuration before adding products.');
      return;
    }
    lock.current = true;
    try {
      const payload = {
        product_id: id,
        expected_updated_at: old?.updated_at,
        adopt_schema: adopt,
        product_type_id: type,
        schema_version_id:
          id &&
          type === original.current?.product_type_id &&
          original.current.schema_version_id == null &&
          !adopt
            ? null
            : profile.data?.schema_version_id || null,
        product_code: id ? code : jerseyEnabled ? '' : code.trim(),
        name: name.trim(),
        category_id: category || null,
        target_audience_id: audience || null,
        age_group_id: age || null,
        description,
        attributes: attrs,
        variants: variants.map(({ key, ...v }) => ({
          ...v,
          sku: jerseyEnabled && !v.id ? '' : v.sku,
        })),
        ...(needsJerseyReview ? { jersey_review: activeReview } : {}),
        deactivate_variant_ids: removed,
        request_key: intent.key('save'),
      };
      const r = entryEnabled
        ? await saveEntry.mutateAsync({
            ...payload,
            configuration_version_id: entry.data!.configuration_version_id,
            recipe_version: 2,
            details: jerseyDetails,
            replace_details: true,
            name: { mode: nameMode, manual_value: name },
            draft_key: !id && codeMode === 'auto' ? draftKey : undefined,
            code: id
              ? code === old?.product_code
                ? { mode: 'retained', value: code }
                : { mode: 'manual', manual_value: code }
              : { mode: codeMode, manual_value: code, value: entryCode },
            variants: variants.map((v) => ({
              ...v,
              client_key: v.key,
              colour: v.color,
              opening_stock: v.id ? 0 : v.stock_quantity,
              expected_updated_at: v.updated_at,
              sku: v.id
                ? v.sku === old?.product_variants.find((p) => p.id === v.id)?.sku
                  ? { mode: 'retained', value: v.sku }
                  : { mode: 'manual', manual_value: v.sku }
                : { mode: v.sku_mode || 'auto', manual_value: v.sku },
            })),
          })
        : await save.mutateAsync(payload);
      intent.complete('save');
      intent.complete('draft-code');
      toast.success('Product saved');
      navigate(`/products/${r.product_id}`);
    } catch (x) {
      setError((x as Error).message);
      if (x instanceof OperationError && x.field) {
        const field = x.field.startsWith('details.')
          ? `jersey-${x.field.split('.')[1]}`
          : x.field
              .replace(/^variants\.(\d+)\./, 'v$1-')
              .replace('opening_stock', 'stock_quantity')
              .replace('colour', 'color');
        setErrors({ [field]: x.message });
        focusInvalid();
      }
    } finally {
      lock.current = false;
    }
  }
  function focusInvalid() {
    requestAnimationFrame(() => {
      const target = formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
      let parent = target?.parentElement;
      while (parent) {
        if (parent instanceof HTMLDetailsElement) parent.open = true;
        parent = parent.parentElement;
      }
      target?.focus();
      target?.scrollIntoView({ block: 'nearest' });
    });
  }
  if ((id && productQuery.isLoading) || choices.isLoading) return <LoadingState />;
  const loadError = (id ? productQuery.error : null) || choices.error;
  if (loadError)
    return (
      <ErrorState
        message={loadError.message}
        onRetry={() => {
          if (id && productQuery.error) void productQuery.refetch();
          if (choices.error) void choices.refetch();
        }}
      />
    );
  if (id && !existing) return <ErrorState message="Product not found" />;
  return (
    <div className="mx-auto grid h-full min-h-0 w-full max-w-5xl grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden">
      <div className="shrink-0">
        <PageHeader
          title={id ? 'Edit product' : 'Add product'}
          description="Choose a category, enter its details, then add variants and optional stock."
          backTo={id ? `/products/${id}` : '/products'}
        />
      </div>
      <form
        id="product-workspace-form"
        ref={formRef}
        onSubmit={submit}
        noValidate
        className="min-h-0 overflow-y-auto overscroll-contain pb-5 pr-1 [scroll-padding-block:1rem] [scrollbar-gutter:stable]"
      >
        <div className="space-y-5">
          {!online && (
            <p role="status" className="rounded-xl border border-border bg-muted p-4 text-sm">
              You are offline. Your current entries are kept. Reconnect before saving or recovering
              a request.
            </p>
          )}
          <fieldset disabled={busy} className="space-y-5">
            <section className="glass-card space-y-4 p-4 sm:p-6">
              <h2 className="font-semibold">1. Product category</h2>
              <label className="block space-y-1 text-sm">
                Category *
                <Select
                  className={input}
                  value={type}
                  aria-invalid={!!errors.category}
                  onChange={(e) => void changeType(e.target.value)}
                  helpLabel="Category *"
                >
                  <option value="">Choose category</option>
                  {choices.data?.types
                    .filter((t) => t.is_active || t.id === type)
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                        {!t.is_active ? ' (inactive)' : ''}
                      </option>
                    ))}
                </Select>
                <FieldHint>
                  Choose the product family first. Its published setup provides the fields below.
                </FieldHint>
              </label>
              {id &&
                type === original.current?.product_type_id &&
                latestSchema &&
                latestSchema !== original.current?.schema_version_id &&
                !adopt && (
                  <button
                    type="button"
                    className="min-h-11 rounded-xl border border-border px-4 text-sm text-primary"
                    onClick={async () => {
                      if (
                        await confirm({
                          title: 'Use the latest category setup?',
                          description:
                            'Saved details are retained. Review the new fields and complete any required values before saving. Existing product codes, SKUs, payments and stock history remain attached to their records.',
                          confirmText: 'Review latest setup',
                        })
                      ) {
                        setVersion(latestSchema);
                        setAdopt(true);
                      }
                    }}
                  >
                    Use latest category setup
                  </button>
                )}
              {adopt && (
                <p role="status" className="text-sm text-muted-foreground">
                  Reviewing the latest setup. Changes take effect only when you save this product.
                </p>
              )}
              {profile.isLoading && (
                <p className="text-sm text-muted-foreground">Loading category fields…</p>
              )}
              {profile.error && (
                <ErrorState message={profile.error.message} onRetry={profile.refetch} />
              )}
              {type && profile.data?.legacy && (
                <p className="rounded-xl bg-amber-500/10 p-3 text-sm">
                  {id
                    ? 'Existing category details remain available. Saving keeps the original product and variant identities.'
                    : 'This category needs a published setup before new products can be saved.'}{' '}
                  {can('master_config.manage') && (
                    <a className="underline" href="/master-config/catalog">
                      Configure category
                    </a>
                  )}
                </p>
              )}
            </section>
            {type && profile.data && (
              <>
                {entryEnabled && entry.data && (
                  <JerseyDetailsFields
                    key={type}
                    value={jerseyDetails}
                    context={entry.data}
                    onChange={setJerseyDetails}
                    errors={Object.fromEntries(
                      Object.entries(errors)
                        .filter(([key]) => key.startsWith('jersey-'))
                        .map(([key, value]) => [key.slice(7), value]),
                    )}
                  />
                )}
                {entryUnavailable && (
                  <p role="alert" className="rounded-xl border border-border bg-muted p-4 text-sm">
                    Optional jersey entry needs its compatible database update before saving. Your
                    entered values are kept.
                  </p>
                )}
                {entry.error && (
                  <ErrorState message={entry.error.message} onRetry={entry.refetch} />
                )}
                <section className="glass-card space-y-4 p-4 sm:p-6">
                  <h2 className="font-semibold">2. Product details</h2>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2 text-sm">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <label htmlFor="workspace-product-name">Product name *</label>
                        {entryEnabled && (
                          <JerseyGenerationMode
                            label="Product name"
                            value={nameMode}
                            onChange={setNameMode}
                            autoDisabled={!suggestedName}
                          />
                        )}
                      </div>
                      <Input
                        id="workspace-product-name"
                        className={input}
                        maxLength={200}
                        value={effectiveName || (entryEnabled && nameMode === 'auto' ? name : '')}
                        aria-invalid={!!errors.name}
                        aria-describedby={errors.name ? 'workspace-name-error' : undefined}
                        onChange={(e) => {
                          setName(e.target.value);
                          setNameMode('manual');
                        }}
                        helpLabel="Product name *"
                      />
                      <FieldHint>
                        {entryEnabled
                          ? 'Type a full name directly, or use Auto to fill it from optional jersey details. Size and colour belong to variants.'
                          : 'Use the recognisable product name. Enter size and colour under Variants.'}
                      </FieldHint>
                      {errors.name && (
                        <p
                          id="workspace-name-error"
                          role="alert"
                          className="text-sm text-destructive"
                        >
                          {errors.name}
                        </p>
                      )}
                      {entryEnabled && suggestedName && nameMode === 'manual' && (
                        <div className="rounded-xl bg-muted/50 p-3 text-xs text-muted-foreground">
                          <span className="block break-words">Suggestion: {suggestedName}</span>
                          <button
                            type="button"
                            className="min-h-11 font-medium text-primary underline"
                            onClick={() => setNameMode('auto')}
                          >
                            Use suggestion
                          </button>
                        </div>
                      )}
                      {entryEnabled && !suggestedName && (
                        <p role="status" className="text-xs text-muted-foreground">
                          No name suggestion yet. Enter a name directly or add any optional detail.
                        </p>
                      )}
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <label htmlFor="workspace-product-code">Product code</label>
                        {entryEnabled && !id && (
                          <JerseyGenerationMode
                            label="Product code"
                            value={codeMode}
                            onChange={setCodeMode}
                          />
                        )}
                      </div>
                      <Input
                        id="workspace-product-code"
                        className={input}
                        value={
                          entryEnabled ? entryCode : jerseyEnabled && !id ? jerseyPreviewCode : code
                        }
                        readOnly={!entryEnabled && (!!id || jerseyEnabled)}
                        aria-invalid={!!errors.code}
                        aria-describedby={errors.code ? 'workspace-code-error' : undefined}
                        onChange={(e) => {
                          setCode(e.target.value.toUpperCase());
                          setCodeMode('manual');
                        }}
                        placeholder={
                          entryEnabled
                            ? effectiveName.trim()
                              ? 'Generating code… or type your own'
                              : 'Enter the product name first'
                            : jerseyEnabled
                              ? 'Choose a team in SKU details'
                              : 'Generated when saved'
                        }
                        helpLabel="Product code"
                      />
                      <FieldHint>
                        {entryEnabled
                          ? id
                            ? 'Edit the code when needed. Previous codes stay reserved for this product.'
                            : codeMode === 'auto'
                              ? 'Generated after you enter the name. You can edit it directly at any time.'
                              : 'Enter a unique code. Auto is available without any jersey details.'
                          : jerseyEnabled
                            ? 'Assigned by the server from the reviewed team. Existing product codes are kept.'
                            : 'Leave blank on a new product to generate a code when saved. Existing codes are kept.'}
                      </FieldHint>
                      {entryEnabled && !editingAvailable && (
                        <p role="alert" className="text-sm text-destructive">
                          Ask an administrator to finish the Jersey identifiers update before
                          generating or saving identifiers. Your entries are kept.
                        </p>
                      )}
                      {entryEnabled && draftCode.error && codeMode === 'auto' && !id && (
                        <p role="alert" className="text-sm text-destructive">
                          {draftCode.error.message}{' '}
                          <button
                            type="button"
                            className="underline"
                            onClick={() => void draftCode.refetch()}
                          >
                            Retry generation
                          </button>
                        </p>
                      )}
                      {errors.code && (
                        <p
                          id="workspace-code-error"
                          role="alert"
                          className="text-sm text-destructive"
                        >
                          {errors.code}
                        </p>
                      )}
                    </div>
                    <label className="space-y-1 text-sm">
                      Subcategory / collection
                      <Select
                        className={input}
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        helpLabel="Subcategory / collection"
                      >
                        <option value="">None</option>
                        {choices.data?.categories
                          .filter(
                            (c) =>
                              c.id === category ||
                              (c.is_active &&
                                (profile.data?.legacy || definition?.category_ids?.includes(c.id))),
                          )
                          .map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                      </Select>
                      <FieldHint>
                        Optional classification within the selected family, e.g. Football Jerseys.
                      </FieldHint>
                    </label>
                    <label className="space-y-1 text-sm">
                      Target audience
                      <Select
                        className={input}
                        value={audience}
                        onChange={(e) => setAudience(e.target.value)}
                        helpLabel="Target audience"
                      >
                        <option value="">None</option>
                        {choices.data?.audiences
                          .filter((a) => a.is_active || a.id === audience)
                          .map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.name}
                            </option>
                          ))}
                      </Select>
                      <FieldHint>Optional: choose who the product is intended for.</FieldHint>
                    </label>
                    <label className="space-y-1 text-sm">
                      Age group
                      <Select
                        className={input}
                        value={age}
                        onChange={(e) => setAge(e.target.value)}
                        helpLabel="Age group"
                      >
                        <option value="">None</option>
                        {choices.data?.ages
                          .filter((a) => a.is_active || a.id === age)
                          .map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.name}
                            </option>
                          ))}
                      </Select>
                      <FieldHint>Optional: choose the intended age range.</FieldHint>
                    </label>
                    <label className="space-y-1 text-sm">
                      Description
                      <Textarea
                        className={input}
                        value={description}
                        maxLength={2000}
                        rows={2}
                        onChange={(e) => setDescription(e.target.value)}
                        helpLabel="Description"
                      />
                      <FieldHint>
                        Optional product details for your team. Keep login secrets out of notes.
                      </FieldHint>
                    </label>
                  </div>
                  <CatalogFields
                    fields={fields.filter(
                      (f) =>
                        f.level === 'product' && !(entryEnabled && JERSEY_HELPER_CODES.has(f.code)),
                    )}
                    values={attrs}
                    onChange={setAttrs}
                    errors={errors}
                  />
                  {!!legacyFields.length && (
                    <details className="rounded-xl border border-border p-3">
                      <summary className="min-h-11 cursor-pointer py-2 text-sm font-medium">
                        Saved additional details ({legacyFields.length})
                      </summary>
                      <CatalogFields
                        fields={legacyFields}
                        values={attrs}
                        onChange={setAttrs}
                        prefix="saved"
                      />
                    </details>
                  )}
                </section>
                {jersey.error && (
                  <p role="alert" className="text-sm text-destructive">
                    {jersey.error.message}{' '}
                    <button
                      type="button"
                      className="min-h-11 underline"
                      onClick={() => void jersey.refetch()}
                    >
                      Retry identifier settings
                    </button>
                  </p>
                )}
                {!entryEnabled && jerseyEnabled && jersey.data && needsJerseyReview && (
                  <JerseySkuReview
                    context={jersey.data}
                    value={activeReview}
                    onChange={setJerseyReview}
                    error={errors.jersey}
                  />
                )}
                <section className="glass-card space-y-4 p-4 sm:p-6">
                  <h2 className="font-semibold">3. Variants and stock</h2>
                  <label className="block max-w-sm space-y-1 text-sm">
                    Add a size set
                    <Select
                      className={input}
                      value=""
                      onChange={(e) => {
                        const selected = choices.data?.sizes.find((s) => s.id === e.target.value);
                        if (!Array.isArray(selected?.sizes)) return;
                        const base = variants[0] || emptyVariant();
                        const next = selected.sizes
                          .filter((size: unknown): size is string => typeof size === 'string')
                          .filter(
                            (size: string) =>
                              !variants.some((v) => v.size === size && v.color === base.color),
                          );
                        setVariants((all) => [
                          ...all,
                          ...next.map((size: string) => ({
                            ...emptyVariant(),
                            size,
                            color: base.color,
                            default_cost: base.default_cost,
                            default_price: base.default_price,
                            low_stock_level: base.low_stock_level,
                          })),
                        ]);
                      }}
                      helpLabel="Add a size set"
                    >
                      <option value="">Choose size set (optional)</option>
                      {choices.data?.sizes
                        .filter((s) => s.is_active)
                        .map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                    </Select>
                    <FieldHint>
                      Adds rows from a prepared size list. Review each variant before saving.
                    </FieldHint>
                  </label>
                  <p className="text-sm text-muted-foreground">
                    Stock is optional. Later additions are recorded in Inventory. Existing variants
                    never receive opening stock again.
                  </p>
                  <FieldHint>
                    Duplicate reuses a variant’s details and suggests a next size where available.
                    Each copy starts with zero opening stock and gets a new SKU when saved.
                  </FieldHint>
                  {variants.map((v, i) => {
                    const variantFields = fields.filter(
                      (f) =>
                        f.level === 'variant' && !(entryEnabled && JERSEY_HELPER_CODES.has(f.code)),
                    );
                    const value = { ...v.attributes, SIZE: v.size, COLOR: v.color };
                    return (
                      <article
                        key={v.key}
                        data-variant-key={v.key}
                        tabIndex={-1}
                        className="scroll-m-4 space-y-4 rounded-xl border border-border p-4 outline-none"
                      >
                        <div className="flex flex-wrap justify-between gap-2">
                          <h3 className="font-medium">Variant {i + 1}</h3>
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              disabled={variants.length >= 500}
                              aria-label={`Duplicate variant ${i + 1}`}
                              onClick={() => duplicateVariant(i)}
                              className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-border px-3 text-sm hover:bg-accent disabled:opacity-50"
                            >
                              <Copy className="h-4 w-4" aria-hidden="true" />
                              Duplicate
                            </button>
                            {(!v.id || can('products.delete')) && (
                              <button
                                type="button"
                                className="min-h-11 px-3 text-sm text-destructive"
                                onClick={async () => {
                                  if (
                                    v.id &&
                                    !(await confirm({
                                      title: 'Deactivate variant?',
                                      description:
                                        'Only variants with no current or reserved stock can be deactivated. History is retained.',
                                      confirmText: 'Deactivate',
                                    }))
                                  )
                                    return;
                                  if (v.id) setRemoved((a) => [...a, v.id!]);
                                  setVariants((a) => a.filter((_, n) => n !== i));
                                }}
                              >
                                {v.id ? 'Deactivate' : 'Remove'}
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          <label className="text-sm">
                            SKU
                            {entryEnabled && !v.id && (
                              <div className="my-2">
                                <JerseyGenerationMode
                                  label={`Variant ${i + 1} SKU`}
                                  value={v.sku_mode || 'auto'}
                                  onChange={(sku_mode) => updateVariant(i, { sku_mode })}
                                />
                              </div>
                            )}
                            <Input
                              className={input}
                              readOnly={!entryEnabled && (!!v.id || jerseyEnabled)}
                              aria-invalid={!!errors[`v${i}-sku`]}
                              value={skuPreview(v)}
                              onChange={(e) =>
                                updateVariant(i, { sku: e.target.value, sku_mode: 'manual' })
                              }
                              placeholder={
                                jerseyEnabled
                                  ? 'Review SKU details, colour and size'
                                  : entryEnabled
                                    ? 'Generated after the product name and code'
                                    : 'Generated when saved'
                              }
                              helpLabel="SKU"
                            />
                            <FieldHint>
                              {entryEnabled
                                ? v.id
                                  ? 'Edit this SKU when needed. Previous SKUs stay reserved for this variant.'
                                  : (v.sku_mode || 'auto') === 'auto'
                                    ? 'Generated from the accepted product code, optional details, colour and size.'
                                    : 'Enter a unique SKU, or choose Auto.'
                                : jerseyEnabled
                                  ? 'Read-only preview. New SKUs are finalized on save; existing SKUs stay unchanged.'
                                  : 'Identifies one variant. Leave blank for a generated SKU on a new variant.'}
                            </FieldHint>
                            {errors[`v${i}-sku`] && (
                              <p role="alert" className="text-sm text-destructive">
                                {errors[`v${i}-sku`]}
                              </p>
                            )}
                            {(jerseyEnabled || entryEnabled) && skuPreview(v) && (
                              <output className="mt-1 block break-all font-mono text-xs text-muted-foreground">
                                {skuPreview(v)}
                              </output>
                            )}
                          </label>
                          {!variantFields.some((f) => f.code === 'SIZE') && (
                            <label className="text-sm">
                              Size
                              <Input
                                data-variant-size
                                className={input}
                                value={v.size}
                                aria-invalid={!!errors[`v${i}-size`]}
                                placeholder="Standard"
                                onChange={(e) => updateVariant(i, { size: e.target.value })}
                                helpLabel="Size"
                              />
                              <FieldHint>
                                Use the selling size, such as M or XL. Use Standard if sizes do not
                                apply.
                              </FieldHint>
                              {errors[`v${i}-size`] && (
                                <p role="alert" className="text-sm text-destructive">
                                  {errors[`v${i}-size`]}
                                </p>
                              )}
                            </label>
                          )}
                          {!variantFields.some((f) => f.code === 'COLOR') && (
                            <label className="text-sm">
                              Colour
                              <Input
                                className={input}
                                value={v.color}
                                aria-invalid={!!errors[`v${i}-color`]}
                                onChange={(e) => updateVariant(i, { color: e.target.value })}
                                helpLabel="Colour"
                              />
                              <FieldHint>
                                Use a consistent colour name, such as Black or Black/Blue.
                              </FieldHint>
                            </label>
                          )}
                          {(['default_cost', 'default_price', 'low_stock_level'] as const).map(
                            (key, n) => (
                              <label key={key} className="text-sm">
                                {['Cost (₹)', 'Sale price (₹)', 'Low-stock alert'][n]}
                                <Input
                                  className={input}
                                  type="number"
                                  min={0}
                                  step={n === 2 ? 1 : '.01'}
                                  value={v[key]}
                                  aria-invalid={!!errors[`v${i}-${key}`]}
                                  aria-describedby={
                                    errors[`v${i}-${key}`]
                                      ? `variant-${v.key}-${key}-error`
                                      : undefined
                                  }
                                  onChange={(e) =>
                                    updateVariant(i, { [key]: Number(e.target.value) })
                                  }
                                  helpLabel={['Cost (₹)', 'Sale price (₹)', 'Low-stock alert'][n]}
                                />
                                <FieldHint>
                                  {
                                    [
                                      'Cost of one unit, in rupees.',
                                      'Selling price of one unit, in rupees.',
                                      'Whole-number threshold for the low-stock warning. This does not change stock.',
                                    ][n]
                                  }
                                </FieldHint>
                                {errors[`v${i}-${key}`] && (
                                  <p
                                    id={`variant-${v.key}-${key}-error`}
                                    role="alert"
                                    className="text-sm text-destructive"
                                  >
                                    {errors[`v${i}-${key}`]}
                                  </p>
                                )}
                              </label>
                            ),
                          )}
                          {!v.id && can('inventory.opening') && (
                            <label className="text-sm">
                              Add stock now
                              <Input
                                className={input}
                                type="number"
                                min={0}
                                step={1}
                                value={v.stock_quantity}
                                aria-invalid={!!errors[`v${i}-stock_quantity`]}
                                onChange={(e) =>
                                  updateVariant(i, { stock_quantity: Number(e.target.value) })
                                }
                                helpLabel="Add stock now"
                              />
                              <FieldHint>
                                New variants only. Zero adds none; enter only units not already
                                recorded in stock.
                              </FieldHint>
                            </label>
                          )}
                        </div>
                        <CatalogFields
                          fields={variantFields}
                          values={value}
                          prefix={`variant-${i}`}
                          errors={Object.fromEntries(
                            Object.entries(errors)
                              .filter(([key]) => key.startsWith(`v${i}-`))
                              .map(([key, s]) => {
                                const fieldKey = key.slice(`v${i}-`.length);
                                return [
                                  fieldKey === 'size' &&
                                  variantFields.some((f) => f.code === 'SIZE')
                                    ? 'SIZE'
                                    : fieldKey === 'color' &&
                                        variantFields.some((f) => f.code === 'COLOR')
                                      ? 'COLOR'
                                      : fieldKey,
                                  s,
                                ];
                              }),
                          )}
                          onChange={(a) => {
                            const { SIZE, COLOR, ...attributes } = a;
                            updateVariant(i, {
                              attributes,
                              size: typeof SIZE === 'string' ? SIZE : v.size,
                              color: typeof COLOR === 'string' ? COLOR : v.color,
                            });
                          }}
                        />
                        {errors[`v${i}`] && (
                          <p role="alert" className="text-sm text-destructive">
                            {errors[`v${i}`]}
                          </p>
                        )}
                      </article>
                    );
                  })}
                  <button
                    type="button"
                    className="min-h-11 rounded-xl border border-border px-4 text-sm"
                    disabled={variants.length >= 500}
                    onClick={() => setVariants((a) => [...a, emptyVariant()])}
                  >
                    + Add variant
                  </button>
                </section>
              </>
            )}
          </fieldset>
          {error && (
            <section className="obsidian-panel space-y-2 rounded-xl border border-destructive/30 bg-card p-4">
              {error && <p className="text-sm text-destructive">{error}</p>}
              <div className="flex flex-wrap items-center justify-between gap-3">
                {error && (
                  <button
                    type="button"
                    className="min-h-11 text-sm underline"
                    onClick={async () => {
                      const fresh = await choices.refetch();
                      const latest = fresh.data?.types.find(
                        (t) => t.id === type,
                      )?.current_schema_version_id;
                      if (!latest) {
                        setError('Publish this category setup first.');
                        return;
                      }
                      if (
                        await confirm({
                          title: 'Review latest category setup?',
                          description:
                            'Your entered details are kept. Review and complete the latest fields before saving.',
                          confirmText: 'Review latest setup',
                        })
                      ) {
                        setVersion(latest);
                        setAdopt(!!id);
                        setError('');
                        setErrors({});
                      }
                    }}
                  >
                    Review latest setup
                  </button>
                )}
                {error && id && (
                  <button
                    type="button"
                    className="min-h-11 text-sm underline"
                    onClick={async () => {
                      if (
                        await confirm({
                          title: 'Reload saved product?',
                          description:
                            'Unsaved edits will be discarded. Recover any uncertain save first.',
                          confirmText: 'Reload product',
                        })
                      )
                        window.location.reload();
                    }}
                  >
                    Reload saved product
                  </button>
                )}
                {error && (
                  <button
                    type="button"
                    className="min-h-11 text-sm underline"
                    onClick={() =>
                      void intent
                        .recover('save')
                        .then((r) => {
                          if (r?.product_id) {
                            intent.complete('draft-code');
                            navigate(`/products/${r.product_id}`);
                          } else setError('No completed save found. Review and retry.');
                        })
                        .catch((e) => setError(e.message))
                    }
                  >
                    Recover previous save
                  </button>
                )}
              </div>
            </section>
          )}
        </div>
      </form>
      <footer className="obsidian-surface z-10 mt-2 space-y-2 rounded-xl border border-border bg-card p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-lg">
        {error && (
          <p role="alert" className="line-clamp-2 text-xs leading-relaxed text-destructive">
            {error}
          </p>
        )}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <span className="text-xs text-muted-foreground">
            {variants.length} variants ·{' '}
            {variants.reduce((sum, variant) => sum + variant.stock_quantity, 0)} units to add
          </span>
          <button
            type="submit"
            form="product-workspace-form"
            disabled={
              busy ||
              !online ||
              entryUnavailable ||
              entry.isFetching ||
              !profile.data ||
              (!id && profile.data.legacy)
            }
            className="min-h-11 w-full rounded-xl bg-primary px-6 font-semibold text-primary-foreground disabled:opacity-50 sm:w-auto"
          >
            {busy ? 'Saving…' : 'Save product'}
          </button>
        </div>
      </footer>
    </div>
  );
}
